import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Fade,
  Alert,
  Divider,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse,
  useTheme,
  alpha,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../../hooks/useAnalytics';
import demoService from '../../../services/demoApi';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import QuizIcon from '@mui/icons-material/Quiz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// Import demo display components for embedded use
import DemoExamDisplay from '../../DemoViewer/components/DemoExamDisplay';
import DemoQuizInterface from '../../DemoViewer/components/DemoQuizInterface';

// Demo topics - will be loaded from API
// Empty placeholder to show while loading
const DEMO_TOPICS_PLACEHOLDER = {};

// Simplified TopicTree component
const DemoTopicTree = ({ 
  topics, 
  selectedTopics, 
  onSelectionChange,
  expansionState,
  onToggleExpand,
  onFoldAll,
  onUnfoldAll
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getAllDescendantPaths = useCallback((node, currentPath) => {
    let paths = [];
    if (!node) return paths;
    if (Array.isArray(node)) {
      node.forEach(leaf => { 
        paths.push(`${currentPath}/${leaf}`); 
      });
    } else if (typeof node === 'object') {
      Object.entries(node).forEach(([childName, childData]) => {
        const childPath = currentPath ? `${currentPath}/${childName}` : childName;
        paths.push(childPath);
        paths = paths.concat(getAllDescendantPaths(childData, childPath));
      });
    }
    return paths;
  }, []);

  const renderTree = useCallback((node, path = '', depth = 0) => {
    const selectedSet = new Set(selectedTopics);
    if (!node) return null;

    // Render leaf nodes
    if (Array.isArray(node)) {
      return node.map((leaf) => {
        const leafPath = `${path}/${leaf}`;
        const isLeafSelected = selectedSet.has(leafPath);
        
        // Filter by search term
        if (searchTerm && !leaf.toLowerCase().includes(searchTerm.toLowerCase())) {
          return null;
        }

        return (
          <ListItem
            key={leafPath}
            sx={{
              pl: 4 + depth * 2,
              py: 0.5,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
              }
            }}
            component="div"
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Checkbox
                edge="start"
                checked={isLeafSelected}
                onChange={() => onSelectionChange(leafPath, !isLeafSelected, [])}
                tabIndex={-1}
                size="small"
              />
            </ListItemIcon>
            <ListItemText
              primary={leaf}
              primaryTypographyProps={{
                variant: 'body2'
              }}
            />
          </ListItem>
        );
      }).filter(Boolean);
    }

    // Render branch nodes
    const entries = Object.entries(node);
    return entries.map(([name, children]) => {
      const currentPath = path ? `${path}/${name}` : name;
      const isExpanded = expansionState[currentPath] || false;
      const hasChildren = children && (
        (Array.isArray(children) && children.length > 0) || 
        (typeof children === 'object' && Object.keys(children).length > 0)
      );
      
      const descendantPaths = getAllDescendantPaths(children, currentPath);
      const hasChildrenSelected = descendantPaths.some(p => selectedSet.has(p));
      const isExactlySelected = selectedSet.has(currentPath);
      const isSelected = isExactlySelected || hasChildrenSelected;
      const isPartiallySelected = hasChildrenSelected && !isExactlySelected;

      // Filter by search term
      if (searchTerm && !name.toLowerCase().includes(searchTerm.toLowerCase()) && !hasChildrenSelected) {
        return null;
      }

      const bgColor = depth === 0
        ? alpha(theme.palette.primary.main, 0.08)
        : depth === 1
          ? alpha(theme.palette.secondary.main, 0.05)
          : 'transparent';

      return (
        <React.Fragment key={currentPath}>
          <ListItem
            onClick={() => {
              if (hasChildren) {
                onToggleExpand(currentPath);
              } else {
                onSelectionChange(currentPath, !isSelected, descendantPaths);
              }
            }}
            sx={{
              pl: 2 + depth * 2,
              py: 0.5,
              cursor: 'pointer',
              borderLeft: depth === 0
                ? `4px solid ${theme.palette.primary.main}`
                : depth === 1
                  ? `4px solid ${theme.palette.secondary.main}`
                  : 'none',
              backgroundColor: bgColor,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
              },
              borderRadius: '4px',
              mb: 0.5,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Checkbox
                edge="start"
                checked={isSelected}
                indeterminate={isPartiallySelected}
                onChange={(event) => {
                  event.stopPropagation();
                  onSelectionChange(currentPath, !isSelected, descendantPaths);
                }}
                tabIndex={-1}
                size="small"
                color="primary"
              />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 36, ml: -2 }}>
              {hasChildren ? (
                <IconButton 
                  size="small" 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    onToggleExpand(currentPath); 
                  }}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              ) : (
                <Box sx={{ width: 24 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={name}
              primaryTypographyProps={{
                variant: depth === 0 ? 'body1' : 'body2',
                sx: {
                  fontWeight: depth === 0 ? 500 : 400,
                  ml: -1
                }
              }}
            />
          </ListItem>
          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderTree(children, currentPath, depth + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    }).filter(Boolean);
  }, [selectedTopics, searchTerm, expansionState, onSelectionChange, onToggleExpand, getAllDescendantPaths, theme]);

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 1.5, sm: 2 }, 
      borderRadius: 2, 
      border: `1px solid ${theme.palette.divider}`,
      height: '100%',
      minHeight: 0,
      overflow: 'hidden'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
          Select Topics
        </Typography>
        {selectedTopics.length > 0 && (
          <Fade in={true} timeout={300}>
            <Chip 
              label={`${selectedTopics.length} selected`}
              color="primary"
              size="small"
            />
          </Fade>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search topics..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Expand All">
          <IconButton size="small" onClick={onUnfoldAll}>
            <UnfoldMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse All">
          <IconButton size="small" onClick={onFoldAll}>
            <UnfoldLessIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.action.hover,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.action.disabled,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
          },
        },
      }}>
        <List dense>
          {renderTree(topics)}
        </List>
      </Box>
      
      {selectedTopics.length > 0 && (
        <Button 
          size="small" 
          onClick={() => onSelectionChange('', false, selectedTopics)}
          sx={{ mt: 1 }}
        >
          Clear all selections
        </Button>
      )}
    </Paper>
  );
};

const ExamGeneratorDemo = ({ onCTAClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  
  // State management
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [topicIdMap, setTopicIdMap] = useState({});
  const [difficulty, setDifficulty] = useState('mixed');
  const [mcqMarks, setMcqMarks] = useState(0);
  const [extendedMarks, setExtendedMarks] = useState(0);
  const [maxMcqMarks, setMaxMcqMarks] = useState(0);
  const [maxExtendedMarks, setMaxExtendedMarks] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expansionState, setExpansionState] = useState({});
  const [showControls, setShowControls] = useState(false);
  const [topics, setTopics] = useState(DEMO_TOPICS_PLACEHOLDER);
  const [generatedExam, setGeneratedExam] = useState(null);
  const [rateLimit, setRateLimit] = useState(null);
  const [viewMode, setViewMode] = useState(null); // null, 'selection', 'view' or 'quiz'
  const [showModeSelection, setShowModeSelection] = useState(false);
  // Frontend-only course selection
  const [selectedCourse, setSelectedCourse] = useState('year12ext1');
  // State for scroll-triggered visibility
  const [hasScrolledBelowFold, setHasScrolledBelowFold] = useState(false);
  const demoContainerRef = React.useRef(null);
  // Loading state for topics
  const [topicsLoading, setTopicsLoading] = useState(true);
  
  // Frontend course options
  const courseOptions = [
    { value: 'year11ext1', label: 'Year 11 Ext 1', available: false },
    { value: 'year12ext1', label: 'Year 12 Ext 1', available: true },
    { value: 'year11adv', label: 'Year 11 Adv', available: false },
    { value: 'year12adv', label: 'Year 12 Adv', available: false },
  ];
  
  const loadTopics = useCallback(async (courseId) => {
    setTopicsLoading(true);
    try {
      const topicsData = await demoService.getTopics(courseId);
      // Handle new format with tree and topic_map
      if (topicsData.tree && topicsData.topic_map) {
        setTopics(topicsData.tree);
        setTopicIdMap(topicsData.topic_map);
      } else {
        // Fallback for old format
        setTopics(topicsData);
        setTopicIdMap({});
      }
    } catch (error) {
      console.error('Failed to load topics:', error);
      // Don't fall back to placeholder - show empty state
      setTopics({});
      setTopicIdMap({});
    } finally {
      setTopicsLoading(false);
    }
  }, []);
  
  // Load topics for the selected course
  useEffect(() => {
    // Load topics from the first available demo course
    const loadCourseTopics = async () => {
      try {
        const courses = await demoService.getCourses();
        if (courses && courses.length > 0) {
          // Use the first course ID
          loadTopics(courses[0].id);
        }
      } catch (error) {
        console.error('Failed to load courses:', error);
      }
    };
    loadCourseTopics();
  }, [loadTopics]);
  
  // Clear rate limit for localhost users on mount
  useEffect(() => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      localStorage.removeItem('lastDemoTime');
      localStorage.removeItem('demoCount');
    }
  }, []);

  // Set up Intersection Observer to detect when demo is scrolled into view
  useEffect(() => {
    if (!demoContainerRef.current || viewMode || showModeSelection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Check if the demo container is in view and has been scrolled past a certain threshold
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setHasScrolledBelowFold(true);
            // Once triggered, we can disconnect the observer
            observer.disconnect();
          }
        });
      },
      {
        threshold: [0, 0.3, 0.5], // Trigger at different visibility levels
        rootMargin: '-20% 0px' // Start showing when 20% into the viewport
      }
    );

    observer.observe(demoContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [viewMode, showModeSelection]);
  
  // Show controls when topics are selected
  useEffect(() => {
    if (selectedTopics.length > 0 && !showControls) {
      setTimeout(() => setShowControls(true), 300);
    } else if (selectedTopics.length === 0) {
      setShowControls(false);
      // Reset marks when no topics selected
      setMcqMarks(0);
      setExtendedMarks(0);
      setMaxMcqMarks(0);
      setMaxExtendedMarks(0);
    }
  }, [selectedTopics, showControls]);
  
  // Fetch available marks when topics or difficulty change
  useEffect(() => {
    const updateAvailableMarks = async () => {
      if (selectedTopics.length === 0) {
        setMaxMcqMarks(0);
        setMaxExtendedMarks(0);
        setMcqMarks(0);
        setExtendedMarks(0);
        return;
      }
      
      try {
        const availableMarks = await demoService.getAvailableMarks(
          selectedTopics,
          difficulty,
          selectedTopicIds
        );
        
        setMaxMcqMarks(availableMarks.mcq);
        setMaxExtendedMarks(availableMarks.extended);
        
        // Always adjust current marks to not exceed available AND demo limits
        const newMcqMarks = Math.min(mcqMarks === 0 ? Math.min(5, availableMarks.mcq) : mcqMarks, Math.min(5, availableMarks.mcq));
        const newExtendedMarks = Math.min(extendedMarks === 0 ? Math.min(10, availableMarks.extended) : extendedMarks, Math.min(10, availableMarks.extended));
        
        setMcqMarks(newMcqMarks);
        setExtendedMarks(newExtendedMarks);
      } catch (error) {
        console.error('Failed to fetch available marks:', error);
        setError('Failed to update available marks based on selection.');
      }
    };

    updateAvailableMarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopics, difficulty, selectedTopicIds]); // Don't include mcqMarks/extendedMarks to avoid infinite loop
  
  // Check rate limiting from API
  const checkRateLimit = useCallback(async () => {
    try {
      const rateLimitData = await demoService.checkRateLimit();
      setRateLimit(rateLimitData);
      // Handle unlimited case for localhost
      if (rateLimitData.remaining === 'unlimited') {
        return true;
      }
      return rateLimitData.remaining > 0;
    } catch (error) {
      console.error('Failed to check rate limit:', error);
      // Fallback to localStorage
      const lastDemoTime = localStorage.getItem('lastDemoTime');
      const demoCount = parseInt(localStorage.getItem('demoCount') || '0');
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      
      if (lastDemoTime) {
        const timeSinceLastDemo = now - parseInt(lastDemoTime);
        if (timeSinceLastDemo < oneHour && demoCount >= 2) {
          return false;
        }
      }
      return true;
    }
  }, []);
  
  const updateRateLimit = useCallback(() => {
    // Skip localStorage update if we have unlimited access
    if (rateLimit && rateLimit.remaining === 'unlimited') {
      return;
    }
    
    const now = Date.now();
    const lastDemoTime = localStorage.getItem('lastDemoTime');
    const oneHour = 60 * 60 * 1000;
    
    if (!lastDemoTime || now - parseInt(lastDemoTime) > oneHour) {
      localStorage.setItem('demoCount', '1');
    } else {
      const count = parseInt(localStorage.getItem('demoCount') || '0') + 1;
      localStorage.setItem('demoCount', count.toString());
    }
    localStorage.setItem('lastDemoTime', now.toString());
  }, [rateLimit]);
  
  const handleCourseChange = useCallback((event) => {
    const newCourse = event.target.value;
    if (newCourse && newCourse !== selectedCourse) {
      const courseOption = courseOptions.find(c => c.value === newCourse);
      if (courseOption && !courseOption.available) {
        // Track attempt to use unavailable course
        trackEvent('Demo Course Restricted', { attempted: newCourse });
        return;
      }
      setSelectedCourse(newCourse);
      // For demo, we always show the same topics from course ID 1
      // No need to reload topics since they're always from course 1
      trackEvent('Demo Course Changed', { course: newCourse });
    }
  }, [selectedCourse, trackEvent, courseOptions]);

  const handleToggleExpand = useCallback((path) => {
    setExpansionState(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);
  
  const handleFoldAll = useCallback(() => {
    setExpansionState({});
  }, []);
  
  const handleUnfoldAll = useCallback(() => {
    // Expand all top-level categories
    const allPaths = {};
    Object.keys(topics).forEach(key => {
      allPaths[key] = true;
      if (typeof topics[key] === 'object') {
        Object.keys(topics[key]).forEach(subKey => {
          allPaths[`${key}/${subKey}`] = true;
        });
      }
    });
    setExpansionState(allPaths);
  }, [topics]);
  
  const handleTopicSelectionChange = useCallback((toggledPath, isSelected, descendantPaths) => {
    // Handle clear all case
    if (toggledPath === '' && !isSelected && descendantPaths.length > 0) {
      setSelectedTopics([]);
      setSelectedTopicIds([]);
      trackEvent('Demo Topics Cleared');
      return;
    }
    
    setSelectedTopics(prevSelected => {
      const newSelected = new Set(prevSelected);
      
      if (isSelected) {
        newSelected.add(toggledPath);
        descendantPaths.forEach(path => newSelected.add(path));
      } else {
        newSelected.delete(toggledPath);
        descendantPaths.forEach(path => newSelected.delete(path));
      }
      
      trackEvent('Demo Topic Selected', {
        topic: toggledPath,
        action: isSelected ? 'selected' : 'deselected',
        total_selected: newSelected.size
      });
      
      return Array.from(newSelected);
    });
    
    // Also update topic IDs
    setSelectedTopicIds(prevIds => {
      const newIds = new Set(prevIds);
      
      if (isSelected) {
        // Add topic ID if available
        if (topicIdMap[toggledPath]) {
          newIds.add(topicIdMap[toggledPath]);
        }
        // Add descendant topic IDs
        descendantPaths.forEach(path => {
          if (topicIdMap[path]) {
            newIds.add(topicIdMap[path]);
          }
        });
      } else {
        // Remove topic ID if available
        if (topicIdMap[toggledPath]) {
          newIds.delete(topicIdMap[toggledPath]);
        }
        // Remove descendant topic IDs
        descendantPaths.forEach(path => {
          if (topicIdMap[path]) {
            newIds.delete(topicIdMap[path]);
          }
        });
      }
      
      return Array.from(newIds);
    });
  }, [trackEvent, topicIdMap]);
  
  const handleDifficultyChange = (event, newDifficulty) => {
    if (newDifficulty !== null) {
      // Only allow 'mixed' difficulty in demo
      if (newDifficulty === 'mixed') {
        setDifficulty(newDifficulty);
        trackEvent('Demo Difficulty Changed', { difficulty: newDifficulty });
      } else {
        // Track attempt to use other difficulties
        trackEvent('Demo Difficulty Restricted', { attempted: newDifficulty });
      }
    }
  };
  
  const handleGenerateDemo = async () => {
    // Check rate limit first
    const canGenerate = await checkRateLimit();
    if (!canGenerate) {
      setError('You\'ve reached the demo limit (2 per hour). Create a free account for unlimited access!');
      trackEvent('Demo Rate Limit Reached');
      return;
    }
    
    if (selectedTopics.length === 0) {
      setError('Please select at least one topic');
      return;
    }
    
    if (mcqMarks === 0 && extendedMarks === 0) {
      setError('Please allocate marks for at least one question type');
      return;
    }
    
    // Validate marks don't exceed available AND demo limits
    if (mcqMarks > Math.min(5, maxMcqMarks)) {
      setError(`Demo limited to 5 MCQ marks. Full app supports up to ${maxMcqMarks} marks.`);
      return;
    }
    
    if (extendedMarks > Math.min(10, maxExtendedMarks)) {
      setError(`Demo limited to 10 extended marks. Full app supports up to ${maxExtendedMarks} marks.`);
      return;
    }
    
    trackEvent('Demo Generation Started', {
      topics: selectedTopics,
      difficulty,
      mcqMarks,
      extendedMarks
    });
    
    setGenerating(true);
    setError(null);
    
    try {
      // Call the API to generate exam with skip_pdf flag
      const result = await demoService.generateExam({
        topicsInclude: selectedTopics,
        topicIds: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
        difficulty,
        mcqMarks,
        extendedMarks
        // PDF now generates in background automatically
      });
      
      setGeneratedExam(result);
      setRateLimit(result.rate_limit_info);
      setShowModeSelection(true); // Show mode selection instead of PDF download
      
      // Update local storage for fallback
      updateRateLimit();
      
      trackEvent('Demo Generation Completed', {
        examId: result.exam_id,
        totalMarks: result.total_marks
      });
    } catch (error) {
      console.error('Demo generation failed:', error);
      
      if (error.isRateLimit) {
        setError(error.message);
        trackEvent('Demo Rate Limit Reached');
      } else if (error.response?.status === 400) {
        setError(error.response.data.detail || 'Invalid request. Please try again.');
      } else {
        setError('Failed to generate demo exam. Please try again.');
      }
      
      trackEvent('Demo Generation Failed', {
        error: error.message || 'Unknown error'
      });
    } finally {
      setGenerating(false);
    }
  };
  
  // Removed unused handleCreateAccount function - navigation handled in handleModeSelect
  
  const handleModeSelect = (mode) => {
    if (!generatedExam?.exam_id) {
      console.error('No exam ID available');
      return;
    }
    
    trackEvent('Demo Mode Selected', {
      mode: mode,
      examId: generatedExam.exam_id
    });
    
    // Set the view mode to show the selected interface
    setViewMode(mode);
    setShowModeSelection(false);
  };
  
  const handleBackToGenerator = () => {
    setViewMode(null);
    setShowModeSelection(false);
    setGeneratedExam(null);
  };
  
  const handleBackToModeSelection = () => {
    setViewMode(null);
    setShowModeSelection(true);
  };
  
  const handleEmbeddedCTAClick = (ctaType, position = null) => {
    trackEvent('Demo CTA Clicked', {
      mode: viewMode,
      cta_type: ctaType,
      position: position,
      examId: generatedExam?.exam_id
    });
    
    // Use the passed onCTAClick prop if available (for modal)
    // Otherwise navigate to waitlist
    if (onCTAClick) {
      onCTAClick();
    } else {
      navigate('/waitlist');
    }
  };
  
  const estimatedDuration = Math.ceil((mcqMarks + extendedMarks) * 1.5);
  
  // Show embedded exam viewer
  if (viewMode === 'view' && generatedExam) {
    return (
      <Box sx={{ 
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pt: { xs: 2, sm: 3 } // Add top padding to push content down into viewport
      }}>
        {/* Header with back button */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton 
            onClick={handleBackToModeSelection}
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Exam View
          </Typography>
        </Box>
        
        {/* Scrollable exam content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          pt: { xs: 4, sm: 5 }, // Add more top padding
          width: '100%'
        }}>
          <DemoExamDisplay 
            examData={generatedExam}
            onCTAClick={handleEmbeddedCTAClick}
            onBackToLanding={handleBackToModeSelection}
            demoAnalytics={{ 
              trackExamViewing: () => {},
              trackQuestionAnswered: () => {},
              trackQuizStart: () => {},
              trackQuizComplete: () => {},
              trackDemoStart: () => {},
              trackCTAClick: () => {},
              getDemoData: () => ({}),
              updateDemoData: () => {},
              trackHintClick: () => {},
              trackAIChatClick: () => {},
              trackQuizCompletion: () => {}
            }} // Simplified analytics with all required methods
          />
        </Box>
      </Box>
    );
  }
  
  // Show embedded quiz mode
  if (viewMode === 'quiz' && generatedExam) {
    return (
      <Box sx={{ 
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pt: { xs: 2, sm: 3 } // Add top padding to push content down into viewport
      }}>
        {/* Header with back button */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton 
            onClick={handleBackToModeSelection}
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Quiz Mode
          </Typography>
        </Box>
        
        {/* Scrollable quiz content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          pt: { xs: 4, sm: 5 }, // Add more top padding
          width: '100%'
        }}>
          <DemoQuizInterface 
            examData={generatedExam}
            onCTAClick={handleEmbeddedCTAClick}
            onBackToLanding={handleBackToModeSelection}
            demoAnalytics={{ 
              trackExamViewing: () => {},
              trackQuestionAnswered: () => {},
              trackQuizStart: () => {},
              trackQuizComplete: () => {},
              trackDemoStart: () => {},
              trackCTAClick: () => {},
              getDemoData: () => ({}),
              updateDemoData: () => {},
              trackHintClick: () => {},
              trackAIChatClick: () => {},
              trackQuizCompletion: () => {}
            }} // Simplified analytics with all required methods
          />
        </Box>
      </Box>
    );
  }
  
  // Show mode selection
  if (showModeSelection && generatedExam) {
    return (
      <Fade in={true}>
        <Box sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ width: '100%', maxWidth: 800 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'primary.main',
                  mb: 3 
                }} 
              />
              <Typography variant="h4" gutterBottom>
                Exam Generated Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {generatedExam.total_marks} marks • {generatedExam.question_counts.mcq} MCQ • {generatedExam.question_counts.extended} Extended
              </Typography>
            </Box>
            
            <Typography variant="h5" sx={{ textAlign: 'center', mb: 3 }}>
              How would you like to practice?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    minHeight: '200px',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => handleModeSelect('view')}
                >
                  <VisibilityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    View Exam
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    See all questions at once in exam format
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    minHeight: '200px',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => handleModeSelect('quiz')}
                >
                  <QuizIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Quiz Mode
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Interactive practice with instant feedback
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="text"
                onClick={handleBackToGenerator}
                sx={{ color: 'text.secondary' }}
              >
                Generate a different exam
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    );
  }
  
  
  return (
    <Box 
      ref={demoContainerRef}
      sx={{ 
        height: { xs: 'auto', md: '100%' }, // Auto height on mobile for scrolling
        minHeight: { xs: '100%', md: 'auto' }, // Ensure it fills container
        display: 'flex', 
        flexDirection: 'column', 
        p: { xs: 1, sm: 2 },
        overflow: { xs: 'visible', md: 'hidden' }, // Allow scrolling on mobile
        // Override parent's center alignment when in view/quiz mode
        ...(viewMode && {
          alignSelf: 'stretch',
          justifyContent: 'stretch'
        })
      }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Course Selection - Only show in generator mode */}
      {!viewMode && !showModeSelection && (
        <Box sx={{ 
          mb: 3,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: 250,
              maxWidth: 300
            }}
          >
            <Select
              value={selectedCourse}
              onChange={handleCourseChange}
              displayEmpty
              sx={{
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1.5,
                  px: 2,
                  textAlign: 'center'
                }
              }}
            >
              {courseOptions.map((course) => (
                <MenuItem 
                  key={course.value} 
                  value={course.value}
                  disabled={!course.available}
                  sx={{
                    fontSize: '0.875rem',
                    justifyContent: 'center'
                  }}
                >
                  {course.label}
                  {!course.available && ' (available in full app)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      
      {/* Placeholder message when not yet scrolled */}
      {!hasScrolledBelowFold && !viewMode && !showModeSelection && (
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          color: 'text.secondary',
          textAlign: 'center',
          p: 4
        }}>
          <Typography variant="h6" sx={{ opacity: 0.7 }}>
            Scroll down to start building your exam
          </Typography>
          <ExpandMoreIcon sx={{ 
            fontSize: 40, 
            opacity: 0.5,
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': {
                transform: 'translateY(0)'
              },
              '40%': {
                transform: 'translateY(-10px)'
              },
              '60%': {
                transform: 'translateY(-5px)'
              }
            }
          }} />
        </Box>
      )}
      
      <Fade in={hasScrolledBelowFold} timeout={800}>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ 
          flex: 1, // Take remaining space after course selection
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 1, sm: 2 },
          overflow: { xs: 'visible', md: 'hidden' },
          opacity: hasScrolledBelowFold ? 1 : 0, // Ensure content is hidden before fade
          visibility: hasScrolledBelowFold ? 'visible' : 'hidden',
          transition: 'opacity 0.8s ease-in-out, visibility 0.8s ease-in-out'
        }}>
          {/* Topic selection - Half width on desktop, full on mobile */}
          <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }} sx={{ height: { xs: 'auto', md: '100%' }, minHeight: { xs: '400px', md: '0' } }}>
          <Box sx={{ 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0
          }}>
            {topicsLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary'
              }}>
                <Typography>Loading topics...</Typography>
              </Box>
            ) : (
              <DemoTopicTree 
                topics={topics}
                selectedTopics={selectedTopics}
                onSelectionChange={handleTopicSelectionChange}
                expansionState={expansionState}
                onToggleExpand={handleToggleExpand}
                onFoldAll={handleFoldAll}
                onUnfoldAll={handleUnfoldAll}
              />
            )}
          </Box>
        </Grid>
        
        {/* Exam parameters - Half width on desktop, full on mobile */}
        <Grid item xs={12} md={6} order={{ xs: 2, md: 2 }} sx={{ height: { xs: 'auto', md: '100%' } }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Fade in={showControls} timeout={500}>
              <Paper elevation={2} sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                    Difficulty
                  </Typography>
                  <ToggleButtonGroup
                    value={difficulty}
                    exclusive
                    onChange={handleDifficultyChange}
                    aria-label="difficulty"
                    fullWidth
                    size="small"
                    sx={{
                      display: 'flex',
                      '& > *': {
                        flex: 1,
                      },
                      '& .MuiToggleButton-root': {
                        py: 1,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                      },
                      '& .MuiToggleButton-root.Mui-disabled': {
                        color: theme.palette.text.disabled,
                        opacity: 0.7,
                      }
                    }}
                  >
                    <ToggleButton value="mixed">Mixed</ToggleButton>
                    <Tooltip title="Available in full app" arrow>
                      <span style={{ flex: 1, display: 'flex' }}>
                        <ToggleButton value="easy" disabled sx={{ width: '100%' }}>
                          Easy
                        </ToggleButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Available in full app" arrow>
                      <span style={{ flex: 1, display: 'flex' }}>
                        <ToggleButton value="medium" disabled sx={{ width: '100%' }}>
                          Medium
                        </ToggleButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Available in full app" arrow>
                      <span style={{ flex: 1, display: 'flex' }}>
                        <ToggleButton value="hard" disabled sx={{ width: '100%' }}>
                          Hard
                        </ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Question Types
                  </Typography>
                  
                  {maxMcqMarks === 0 && maxExtendedMarks === 0 && selectedTopics.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No demo questions available for the selected topics and difficulty. 
                      Try selecting different topics or changing the difficulty.
                    </Alert>
                  )}
                  
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>
                        Multiple Choice
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mcqMarks} / {Math.min(5, maxMcqMarks)} marks
                        {maxMcqMarks > 5 && (
                          <Tooltip title="Up to 20 marks available in full app" arrow>
                            <span style={{ color: theme.palette.primary.main, cursor: 'help' }}> (+{maxMcqMarks - 5})</span>
                          </Tooltip>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                      <Slider
                        value={Math.min(mcqMarks, 5)}
                        onChange={(e, newValue) => setMcqMarks(Math.min(newValue, 5))}
                        min={0}
                        max={Math.min(5, maxMcqMarks)}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        size="small"
                        disabled={maxMcqMarks === 0}
                      />
                      {maxMcqMarks > 5 && (
                        <Box sx={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic'
                        }}>
                          <Tooltip title="More marks available in full app" arrow>
                            <span style={{ cursor: 'help' }}>Demo limit: 5</span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, mt: 3 }}>
                      <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>
                        Extended Response
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {extendedMarks} / {Math.min(10, maxExtendedMarks)} marks
                        {maxExtendedMarks > 10 && (
                          <Tooltip title="Up to 40 marks available in full app" arrow>
                            <span style={{ color: theme.palette.primary.main, cursor: 'help' }}> (+{maxExtendedMarks - 10})</span>
                          </Tooltip>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                      <Slider
                        value={Math.min(extendedMarks, 10)}
                        onChange={(e, newValue) => setExtendedMarks(Math.min(newValue, 10))}
                        min={0}
                        max={Math.min(10, maxExtendedMarks)}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        size="small"
                        disabled={maxExtendedMarks === 0}
                      />
                      {maxExtendedMarks > 10 && (
                        <Box sx={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic'
                        }}>
                          <Tooltip title="More marks available in full app" arrow>
                            <span style={{ cursor: 'help' }}>Demo limit: 10</span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                
                {/* Total marks section at bottom */}
                <Box sx={{ 
                  mt: 'auto',
                  pt: 2
                }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
                    p: 1.5,
                    borderRadius: 1
                  }}>
                    <Box>
                      <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                        Total: {mcqMarks + extendedMarks} marks
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Est. duration: {estimatedDuration} minutes
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
              </Paper>
            </Fade>
          </Box>
        </Grid>
      </Grid>
      </Fade>
      
      {/* Generate button - Centered at bottom */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        mt: 2,
        py: 2
      }}>
        {showControls && (
          <Fade in={true} timeout={500}>
            <Button
              variant="contained"
              onClick={handleGenerateDemo}
              disabled={
                generating || 
                selectedTopics.length === 0 || 
                (mcqMarks === 0 && extendedMarks === 0) ||
                (maxMcqMarks === 0 && maxExtendedMarks === 0) ||
                mcqMarks > maxMcqMarks ||
                extendedMarks > maxExtendedMarks
              }
              startIcon={generating ? null : <AutoAwesomeIcon />}
              size="large"
              sx={{ 
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              {generating ? 'Generating Demo...' : 'Generate Demo Exam'}
            </Button>
          </Fade>
        )}
      </Box>
      
      {generating && (
        <Box
          sx={{
            position: { xs: 'fixed', md: 'absolute' },
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  margin: '0 auto',
                  borderRadius: '50%',
                  border: '3px solid',
                  borderColor: 'primary.light',
                  borderTopColor: 'primary.main',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
            </Box>
            <Typography variant="h6" color="primary">
              Generating Your Demo Exam
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This usually takes a few seconds...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ExamGeneratorDemo;